import { useContext, useLayoutEffect } from 'react';

import { ViewerContext } from '@vimeo/viewer';

import { ViewerAiContainer } from './ViewerAiModule.style';
import { QuestionContextProvider } from '../../contexts/QuestionContext';
import { QuestionsDataContextProvider } from '../../contexts/QuestionsDataContext';
import { TokensContextProvider } from '../../contexts/TokensContext';
import { useContainerDataStore } from '../../stores/container-data-store';
import type { IInheritColors } from '../../stores/viewer-ui-store';
import { useUIStore } from '../../stores/viewer-ui-store';
import { ErrorBoundary } from '../ErrorBoundary';
import type { TOverrideBreakpoints } from '../ViewerQnA/constants';
import { ViewerQnA } from '../ViewerQnA/ViewerQnA';

interface ViewerAiModuleProps {
  videoId: number;
  videoRequestId: string;
  pageName: 'player' | 'viewer_home';
  guestSignature?: string;
  overrideBreakpoint?: TOverrideBreakpoints;
  onClose?: () => void;
  onMomentPlay: (timecode: number) => void;
  onLoginRequired: () => void;
  inheritColors?: IInheritColors | null;
}

export const ViewerAiModule = ({
  videoId,
  videoRequestId,
  pageName,
  onClose,
  onMomentPlay,
  onLoginRequired,
  overrideBreakpoint,
  inheritColors,
  guestSignature,
}: ViewerAiModuleProps) => {
  const viewer = useContext(ViewerContext);
  const initializeContainerData = useContainerDataStore((state) => state.initialize);
  const resetContainerData = useContainerDataStore((state) => state.reset);

  const initializeUIStore = useUIStore((state) => state.initializeUIStore);
  const resetUIStore = useUIStore((state) => state.reset);

  useLayoutEffect(function handleInitialization() {
    // Initialize with the videoId from first render
    initializeContainerData(videoId, pageName);
    initializeUIStore(inheritColors, overrideBreakpoint);

    return () => {
      resetContainerData();
      resetUIStore();
    };
    // Empty dependency array ensures this only runs once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorBoundary viewer={viewer}>
      <ViewerAiContainer data-testid="viewer-ai-module">
        <QuestionsDataContextProvider videoId={videoRequestId} guestSignature={guestSignature}>
          <QuestionContextProvider videoId={videoRequestId} guestSignature={guestSignature}>
            <TokensContextProvider videoId={videoId}>
              <ViewerQnA
                onMomentPlay={onMomentPlay}
                onClose={onClose}
                videoId={videoRequestId}
                onLoginRequired={onLoginRequired}
                isPlayer={pageName === 'player'}
              />
            </TokensContextProvider>
          </QuestionContextProvider>
        </QuestionsDataContextProvider>
      </ViewerAiContainer>
    </ErrorBoundary>
  );
};
